import React, {useLayoutEffect, useRef, useState} from 'react';

import {Content, DropDown, MoreIcon, Trigger} from '@kit';

import {cls} from '../../../../components/utils';
import {useObserver} from '../../../../components/utils/hooks';

import css from './sheet.module.css';

type NavIcon = React.FC<{appearance?: 'regular' | 'filled'; size?: 24}>;

export type NavScreen = {
	key: string;
	title: string;
	Icon: NavIcon;
	/** точка на иконке: на экране есть что сделать */
	hasBadge?: boolean;
};

/** меньше двух экранов за «Ещё» не прячем: кнопка «Ещё» с одним пунктом места не экономит */
const MIN_HIDDEN = 2;

type Props = {
	screens: NavScreen[];
	current: string;
	onOpen(key: string): void;
};

/**
 * нижняя навигация узкого экрана, как в Android; кнопки занимают ширину по своей подписи, а если ряд не
 * помещается, последние экраны по одному уходят за кнопку «Ещё»
 */
export const BottomNav: React.FC<Props> = ({screens, current, onOpen}) => {
	const navRef = useRef<HTMLElement>(null);
	const {width} = useObserver(navRef, {throttleTimeout: 50});
	const [isMoreOpen, setMoreOpen] = useState(false);
	const [overflow, setOverflow] = useState({width: 0, hiddenCount: 0});
	const hiddenCount = overflow.width === width ? overflow.hiddenCount : 0;
	const primary = screens.slice(0, screens.length - hiddenCount);
	const secondary = screens.slice(screens.length - hiddenCount);
	const isMoreActive = secondary.some(({key}) => key === current);

	useLayoutEffect(() => {
		const nav = navRef.current;
		if (!nav || nav.scrollWidth <= nav.clientWidth || hiddenCount >= screens.length - 1) return;
		setOverflow({width, hiddenCount: Math.max(MIN_HIDDEN, hiddenCount + 1)});
	});

	return (
		<nav ref={navRef} className={css.bottomNav} aria-label="Экраны бланка" data-bottom-nav>
			{primary.map((screen) => (
				<NavButton key={screen.key} screen={screen} isActive={screen.key === current} onClick={onOpen} />
			))}
			{secondary.length > 0 && (
				<DropDown className={css.bottomNavMore} open={isMoreOpen} onOpenChange={setMoreOpen}>
					<Trigger>
						<button
							type="button"
							className={cls(css.bottomNavButton, isMoreActive && css.bottomNavActive)}
							aria-current={isMoreActive ? 'page' : undefined}
						>
							<span className={css.bottomNavIcon}>
								<MoreIcon size={24} />
								{secondary.some(({hasBadge}) => hasBadge) && <span className={css.tabBadge} />}
							</span>
							Ещё
						</button>
					</Trigger>
					<Content align="end" className={css.bottomNavMenu}>
						{secondary.map((screen) => (
							<NavButton
								key={screen.key}
								screen={screen}
								isActive={screen.key === current}
								isInMenu
								onClick={(key) => {
									setMoreOpen(false);
									onOpen(key);
								}}
							/>
						))}
					</Content>
				</DropDown>
			)}
		</nav>
	);
};

type NavButtonProps = {
	screen: NavScreen;
	isActive: boolean;
	/** пункт в меню «Ещё»: иконка слева от подписи */
	isInMenu?: boolean;
	onClick(key: string): void;
};

const NavButton: React.FC<NavButtonProps> = ({screen: {key, title, Icon, hasBadge}, isActive, isInMenu, onClick}) => (
	<button
		type="button"
		className={cls(isInMenu ? css.bottomNavMenuItem : css.bottomNavButton, isActive && css.bottomNavActive)}
		aria-current={isActive ? 'page' : undefined}
		onClick={() => onClick(key)}
	>
		<span className={css.bottomNavIcon}>
			<Icon appearance={isActive ? 'filled' : 'regular'} size={24} />
			{hasBadge && <span className={css.tabBadge} />}
		</span>
		{title}
	</button>
);
